import {
  Box,
  BoxProps,
  Flex,
  Grid,
  GridItem,
  Heading,
  List,
  ListItem,
  Tag,
} from "@chakra-ui/react";
import styled from "@emotion/styled";
import * as fs from "fs";
import { GetStaticProps } from "next";
import Head from "next/head";
import { useRouter } from "next/router";
import * as path from "path";
import FullPageCenteredSpinner from "../../components/FullPageCenteredSpinner";

const Underline = styled("div")`
  position: relative;
  &::after {
    content: "";

    width: 100%;
    position: absolute;
    left: 0;
    bottom: 1px;

    border-width: 0 0 1px;
    border-style: solid;
  }
`;

function Section({
  heading,
  children,
  ...restProps
}: BoxProps & { heading?: React.ReactNode }) {
  return (
    <Box as="section" {...restProps}>
      {heading ? <Underline>{heading}</Underline> : null}

      {children}
    </Box>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  const resumeFile = path.join(process.cwd(), "assets", "resume.json");

  try {
    const file = await new Promise<object>((resolve, reject) => {
      fs.readFile(resumeFile, (err, file) => {
        try {
          if (err) {
            reject(err);
          } else {
            resolve(JSON.parse(file.toString()));
          }
        } catch (e) {
          reject(err);
        }
      });
    });

    return {
      props: {
        resume: file,
      },
      revalidate: 60 * 10,
    };
  } catch {
    return {
      redirect: {
        destination: "/",
        permanent: false,
      },
    };
  }
};

export default function Index({ resume }) {
  const router = useRouter();

  if (router.isFallback) {
    return <FullPageCenteredSpinner />;
  }

  return (
    <>
      <Head>
        <title>Matthew Costabile | Resume</title>
      </Head>
      <Grid
        gridTemplateAreas={`
            "header header"
            "aside main"
          `}
        gridTemplateColumns="0.6fr 1.4fr"
        gridTemplateRows="auto 1fr"
        gridGap="1em"
        padding={3}
        height="100%"
      >
        <GridItem gridArea="header">
          <Heading as="h1" size="4xl">
            {resume.basics.name}
          </Heading>
          <Heading size="3xl">{resume.basics.label}</Heading>
        </GridItem>
        <GridItem gridArea="aside" as="aside">
          <Section heading={<Heading size="xl">About</Heading>}>
            <List>
              <ListItem>
                {resume.basics.location.city}, {resume.basics.location.region}{" "}
                {resume.basics.location.postalCode}
              </ListItem>
            </List>
          </Section>

          {resume.skills.map((skill) => {
            return (
              <Section key={skill.name} heading={skill.name}>
                <List display="flex" flexWrap="wrap">
                  {skill.keywords.map((keyword) => {
                    return (
                      <ListItem key={keyword} p={1}>
                        <Tag>{keyword}</Tag>
                      </ListItem>
                    );
                  })}
                </List>
              </Section>
            );
          })}

          <Section heading={<Heading size="xl">Languages</Heading>}>
            <List>
              {resume.languages.map((language) => {
                return (
                  <ListItem key={language.language}>
                    {language.language} <em>({language.fluency})</em>
                  </ListItem>
                );
              })}
            </List>
          </Section>
          <Section heading={<Heading size="xl">Interests</Heading>}>
            <List>
              {resume.interests.map((interest) => {
                return (
                  <ListItem key={interest.name}>
                    {interest.name}
                    <List display="flex" flexWrap="wrap">
                      {interest.keywords.map((keyword) => {
                        return (
                          <ListItem key={keyword} p={1}>
                            <Tag>{keyword}</Tag>
                          </ListItem>
                        );
                      })}
                    </List>
                  </ListItem>
                );
              })}
            </List>
          </Section>
        </GridItem>
        <GridItem gridArea="main">
          <Section heading={<Heading size="xl">Summary</Heading>}>
            <Box as="p" p={2}>
              {resume.basics.summary}
            </Box>
          </Section>
          <Section heading={<Heading size="xl">Work</Heading>} pb={2}>
            <List>
              {resume.work.map((work) => {
                return (
                  <ListItem
                    key={work.company + work.startDate + work.endDate}
                    p={2}
                  >
                    <Section
                      heading={
                        <>
                          <Flex
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Heading size="lg">{work.company}</Heading>
                            <Heading size="sm">
                              (
                              {`${work.startDate} - ${
                                work.endDate ?? "Current"
                              }`}
                              )
                            </Heading>
                          </Flex>

                          <Heading size="md">{work.position}</Heading>
                        </>
                      }
                    >
                      {work.summary}
                      <List listStyleType="disc" paddingLeft={4}>
                        {work.highlights.map((highlight) => {
                          return (
                            <ListItem key={highlight}>{highlight}</ListItem>
                          );
                        })}
                      </List>
                    </Section>
                  </ListItem>
                );
              })}
            </List>
          </Section>
          <Section heading={<Heading size="xl">Education</Heading>} pb={2}>
            <List listStyleType="disc" paddingLeft={4}>
              {resume.education.map((education) => {
                return (
                  <ListItem
                    key={
                      education.institution +
                      education.startDate +
                      education.endDate
                    }
                  >
                    <Section
                      heading={
                        <>
                          <Flex
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Heading size="lg">{education.institution}</Heading>
                            <Heading size="sm">
                              (
                              {`${education.startDate} - ${
                                education.endDate ?? "Current"
                              }`}
                              )
                            </Heading>
                          </Flex>

                          <Heading size="md">
                            {education.studyType}: {education.area}
                          </Heading>
                        </>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Section>
        </GridItem>
      </Grid>
    </>
  );
}
